import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Banknote } from "lucide-react";
import type { BillingTransaction } from "@/types/billing-transactions";
import { cn } from "@/lib/utils";
import { computeBalanceStats } from "../../utils/computeBalanceStats";
import { formatMoney } from "../../utils/formatMoney";

interface TransactionBalanceSummaryProps {
  transactions: BillingTransaction[];
}

export const BalanceSummary = ({
  transactions,
}: TransactionBalanceSummaryProps) => {
  const { t } = useTranslation();

  const stats = useMemo(
    () => computeBalanceStats(transactions),
    [transactions],
  );

  return (
    <div className="w-full my-2 flex items-center gap-2">
      <div className="flex-1 flex flex-row justify-between p-4 border border-neutral-200 rounded-2xl bg-light-zinc">
        <div className="flex flex-row gap-2">
          <Banknote className="w-6 h-6 text-light-purple" />
          <span className="text-zinc text-base">{t("billing.myBalance")}</span>
        </div>

        <span
          className={cn(
            "text-base font-semibold",
            stats.balanceMinor < 0 ? "text-red-600" : "text-green-600",
          )}
        >
          {formatMoney(stats.balanceMinor, stats.currency)}
        </span>
      </div>

      <div className="flex-1 flex flex-row justify-between p-4 border border-neutral-200 rounded-2xl bg-light-zinc">
        <div className="flex flex-row gap-2">
          <ArrowUp className="w-6 h-6 text-light-purple" />
          <span className="text-zinc text-base">{t("billing.credit")}</span>
        </div>

        <span className="text-green-600 text-base font-semibold">
          {formatMoney(stats.creditMinor, stats.currency)}
        </span>
      </div>

      <div className="flex-1 flex flex-row justify-between p-4 border border-neutral-200 rounded-2xl bg-light-zinc">
        <div className="flex flex-row gap-2">
          <ArrowDown className="w-6 h-6 text-light-purple" />
          <span className="text-zinc text-base">{t("billing.debet")}</span>
        </div>

        <span className="text-red-600 text-base font-semibold">
          {formatMoney(stats.debetMinor, stats.currency)}
        </span>
      </div>
    </div>
  );
};
